import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { batchesAPI } from '../../services/api';
import { useToast } from '../ui/Toast';

interface Product {
  product_id: number;
  name: string;
  description?: string;
  price: string | number;
  status: 'Active' | 'Inactive' | 'Discontinued';
  created_at: string;
  updated_at: string;
}

interface Batch {
  batch_id: number;
  product_id: number;
  production_date: string;
  created_at: string;
  batch_number?: string;
  product?: Product;
}

interface BatchFormModalProps {
  batch?: Batch | null;
  products: Product[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const BatchFormModal: React.FC<BatchFormModalProps> = ({
  batch,
  products,
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    production_date: '',
  });
  const { addToast } = useToast();

  useEffect(() => {
    if (batch) {
      setFormData({
        product_id: batch.product_id.toString(),
        production_date: batch.production_date.split('T')[0], // Format for date input
      });
    } else {
      setFormData({
        product_id: '',
        production_date: '',
      });
    }
  }, [batch, open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (batch) {
        // Update existing batch
        const updateData = {
          product_id: parseInt(formData.product_id),
          production_date: formData.production_date,
        };

        await batchesAPI.update(batch.batch_id, updateData);
        addToast({ type: 'success', title: 'Batch updated successfully' });
      } else {
        // Create new batch
        const createData = {
          product_id: parseInt(formData.product_id),
          production_date: formData.production_date,
        };

        await batchesAPI.create(createData);
        addToast({ type: 'success', title: 'Batch created successfully' });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      addToast({ type: 'error', title: 'Error', message: err.message || 'Failed to save batch' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {batch ? 'Edit Batch' : 'Create Batch'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {/* Product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Product *
              </label>
              <Select 
                value={formData.product_id} 
                onValueChange={(value) => handleSelectChange('product_id', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.product_id} value={product.product_id.toString()}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Production Date */}
            <div>
              <label htmlFor="production_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Production Date *
              </label>
              <Input
                type="date"
                id="production_date"
                name="production_date"
                value={formData.production_date}
                onChange={handleInputChange}
                required
                max={new Date().toISOString().split('T')[0]} // Can't be in the future
                className="w-full"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-600">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !formData.product_id || !formData.production_date}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Saving...' : (batch ? 'Update Batch' : 'Create Batch')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};